import { useCallback, useEffect, useRef, useState } from 'react';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Drawer from '../../components/Drawer';
import ThemeToggle from '../../components/ThemeToggle';
import SignaturePad from '../../components/SignaturePad';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { api } from '../../api/client';
import { money, date } from '../../lib/format';

const TABS = [
  { key: 'invoices', label: 'Invoices' },
  { key: 'quotes', label: 'Quotes' },
  { key: 'payments', label: 'Payments' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'site-visits', label: 'Site visits' },
  { key: 'w9', label: 'W-9' },
];

export default function PortalDashboard() {
  const { portal, logout } = usePortalAuth();
  const [tab, setTab] = useState('invoices');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteDoc, setQuoteDoc] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payMsg, setPayMsg] = useState(null);
  const [w9, setW9] = useState(null);
  const [w9Note, setW9Note] = useState('');
  const [w9Busy, setW9Busy] = useState(false);
  const [w9Msg, setW9Msg] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [agrOpen, setAgrOpen] = useState(null);
  const [agrName, setAgrName] = useState('');
  const [agrBusy, setAgrBusy] = useState(false);
  const [agrErr, setAgrErr] = useState(null);
  const agrSig = useRef(null);
  const [approvals, setApprovals] = useState([]);
  const [apprForm, setApprForm] = useState({});
  const [apprBusy, setApprBusy] = useState(null);

  // Show a banner when returning from Stripe Checkout.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const pay = p.get('pay');
    if (pay === 'success') setPayMsg({ ok: true, text: 'Payment submitted — thank you! Card payments post right away; bank (ACH) payments take a few business days to clear before they show here.' });
    else if (pay === 'cancel') setPayMsg({ ok: false, text: 'Payment canceled. No charge was made.' });
    if (pay) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  useEffect(() => { api('/portal/summary').then(setSummary).catch(() => setSummary(null)); }, []);

  // Deep link from an emailed quote/invoice: land on the right tab and open the invoice.
  useEffect(() => {
    let dl = null;
    try {
      const raw = sessionStorage.getItem('fd_portal_deeplink');
      if (raw) { dl = JSON.parse(raw); sessionStorage.removeItem('fd_portal_deeplink'); }
    } catch { /* ignore */ }
    if (dl && dl.id) {
      if (dl.tab) setTab(dl.tab);
      if (dl.tab === 'invoices') openInvoice({ id: dl.id });
      else if (dl.tab === 'quotes') openQuote({ id: dl.id });
      const p = new URLSearchParams(window.location.search);
      if (p.get('invoice') || p.get('quote')) window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (tab === 'w9') {
      setLoading(true);
      api('/portal/w9-request').then(setW9).catch(() => setW9(null)).finally(() => setLoading(false));
      return;
    }
    if (tab === 'contracts') {
      setLoading(true);
      api('/portal/contracts').then((c) => setContracts(Array.isArray(c) ? c : [])).catch(() => setContracts([])).finally(() => setLoading(false));
      return;
    }
    if (tab === 'approvals') {
      setLoading(true);
      api('/portal/approvals').then((a) => setApprovals(Array.isArray(a) ? a : [])).catch(() => setApprovals([])).finally(() => setLoading(false));
      return;
    }
    if (tab === 'agreements') {
      setLoading(true); setAgrOpen(null);
      api('/portal/agreements').then((a) => setAgreements(Array.isArray(a) ? a : [])).catch(() => setAgreements([])).finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    api(`/portal/${tab}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  async function openAgreement(id) {
    setAgrErr(null); setAgrName(portal?.customer_name || '');
    try { setAgrOpen(await api(`/portal/agreements/${id}`)); } catch (e) { setAgrErr(e?.detail || 'Could not open.'); }
  }
  async function signAgreement() {
    if (!agrSig.current || agrSig.current.isEmpty()) { setAgrErr('Please add your signature.'); return; }
    if (!agrName.trim()) { setAgrErr('Please type your name.'); return; }
    setAgrBusy(true); setAgrErr(null);
    try {
      await api(`/portal/agreements/${agrOpen.id}/sign`, { method: 'POST', body: { name: agrName.trim(), signature_data: agrSig.current.toTrimmedDataURL() } });
      setAgrOpen(null);
      api('/portal/agreements').then((a) => setAgreements(Array.isArray(a) ? a : [])).catch(() => {});
      api('/portal/summary').then(setSummary).catch(() => {});
    } catch (e) { setAgrErr(e?.detail || 'Could not submit.'); }
    finally { setAgrBusy(false); }
  }

  async function decide(id, decision) {
    setApprBusy(id);
    setApprForm((s) => ({ ...s, [id]: { ...(s[id] || {}), err: null } }));
    try {
      const f = apprForm[id] || {};
      await api(`/portal/approvals/${id}/decision`, { method: 'POST', body: { decision, name: (f.name || portal?.customer_name || '').trim() || null, comments: (f.comments || '').trim() || null } });
      api('/portal/approvals').then((a) => setApprovals(Array.isArray(a) ? a : [])).catch(() => {});
      api('/portal/summary').then(setSummary).catch(() => {});
    } catch (e) {
      setApprForm((s) => ({ ...s, [id]: { ...(s[id] || {}), err: e?.detail || 'Could not submit.' } }));
    } finally { setApprBusy(null); }
  }

  async function requestW9() {
    setW9Busy(true); setW9Msg(null);
    try {
      await api('/portal/w9-request', { method: 'POST', body: { note: w9Note.trim() || null } });
      setW9Note(''); setW9Msg({ ok: true, text: 'Request submitted. We\u2019ll review it and send your W-9.' });
      api('/portal/w9-request').then(setW9).catch(() => {});
    } catch (e) { setW9Msg({ ok: false, text: e?.detail || 'Could not submit the request.' }); }
    finally { setW9Busy(false); }
  }

  async function openInvoice(row) {
    setInvLoading(true); setInvoice({ loading: true }); setQuote(null);
    try {
      setInvoice(await api(`/portal/invoices/${row.id}`));
      api(`/portal/invoices/${row.id}/pay-quote`).then(setQuote).catch(() => setQuote(null));
    }
    catch { setInvoice(null); }
    finally { setInvLoading(false); }
  }

  async function openQuote(row) {
    setQuoteLoading(true); setQuoteDoc({ loading: true });
    try { setQuoteDoc(await api(`/portal/quotes/${row.id}`)); }
    catch { setQuoteDoc(null); }
    finally { setQuoteLoading(false); }
  }

  async function startPay(method) {
    if (!invoice?.id) return;
    setPaying(method);
    try {
      const r = await api(`/portal/invoices/${invoice.id}/pay`, { method: 'POST', body: { method } });
      if (r?.url) window.location.href = r.url;
      else { setPayMsg({ ok: false, text: 'Could not start the payment.' }); setPaying(null); }
    } catch (e) {
      setPayMsg({ ok: false, text: e?.detail || 'Could not start the payment.' });
      setPaying(null);
    }
  }

  const columns = {
    invoices: [
      { key: 'invoice_number', header: 'Invoice' },
      { key: 'issue_date', header: 'Date', render: (r) => date(r.issue_date) },
      { key: 'due_date', header: 'Due', render: (r) => (r.due_date ? date(r.due_date) : '—') },
      { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
      { key: 'balance', header: 'Balance', render: (r) => <span className="num">{money(r.balance)}</span> },
      { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    ],
    quotes: [
      { key: 'quote_number', header: 'Quote' },
      { key: 'issue_date', header: 'Date', render: (r) => date(r.issue_date) },
      { key: 'expiry_date', header: 'Valid until', render: (r) => (r.expiry_date ? date(r.expiry_date) : '—') },
      { key: 'total', header: 'Total', render: (r) => <span className="num">{money(r.total)}</span> },
      { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    ],
    payments: [
      { key: 'payment_date', header: 'Date', render: (r) => date(r.payment_date) },
      { key: 'method', header: 'Method', render: (r) => (r.method || '').toUpperCase() },
      { key: 'reference', header: 'Reference', render: (r) => r.reference || '—' },
      { key: 'applied', header: 'Applied to', render: (r) => (r.applied || []).map((a) => `${a.invoice_number}`).join(', ') || '—' },
      { key: 'amount', header: 'Amount', render: (r) => <span className="num">{money(r.amount)}</span> },
    ],
    expenses: [
      { key: 'expense_date', header: 'Date', render: (r) => date(r.expense_date) },
      { key: 'vendor', header: 'Vendor', render: (r) => r.vendor || '—' },
      { key: 'invoice_number', header: 'On invoice', render: (r) => r.invoice_number || '—' },
      { key: 'total', header: 'Amount', render: (r) => <span className="num">{money(r.total)}</span> },
    ],
  }[tab];

  const emptyMsg = {
    invoices: 'No invoices yet.',
    quotes: 'No quotes yet.',
    payments: 'No payments recorded yet.',
    expenses: 'No billed expenses yet.',
  }[tab];

  const visibleTabs = (() => {
    const mid = [];
    if (summary?.has_agreements) mid.push({ key: 'agreements', label: 'Agreements' });
    if (summary?.has_approvals) mid.push({ key: 'approvals', label: 'Approvals' });
    if (summary?.has_contracts) mid.push({ key: 'contracts', label: 'Contracts' });
    return [...TABS.slice(0, 5), ...mid, ...TABS.slice(5)];
  })();

  return (
    <div className="portal-shell">
      {portal?.impersonated && (
        <div className="imp-bar">
          <span><strong>Admin preview</strong> — viewing as {portal?.customer_name || 'customer'}. Read-only; actions are disabled.</span>
          <button className="imp-bar__exit" onClick={logout}>Exit preview</button>
        </div>
      )}
      <header className="portal-header">
        <div>
          <div className="portal-header__biz">{portal?.business_name || 'Customer Portal'}</div>
          <div className="portal-header__who">{portal?.customer_name}</div>
        </div>
        <div className="portal-header__actions">
          <ThemeToggle />
          <button className="btn btn--sm" onClick={logout}>Sign out</button>
        </div>
      </header>

      <main className="portal-main">
        {payMsg && (
          <div className={`portal-banner ${payMsg.ok ? 'portal-banner--ok' : 'portal-banner--warn'}`}>
            <span>{payMsg.text}</span>
            <button className="portal-banner__x" onClick={() => setPayMsg(null)}>×</button>
          </div>
        )}
        {summary && (
          <div className="portal-summary">
            <div className="portal-summary__label">Outstanding balance</div>
            <div className="portal-summary__amount">{money(summary.outstanding)}</div>
            {summary.open_count > 0 && <div className="portal-summary__sub">{summary.open_count} open invoice{summary.open_count === 1 ? '' : 's'}</div>}
          </div>
        )}

        {summary?.pending_agreements > 0 && tab !== 'agreements' && (
          <button className="portal-banner portal-banner--warn portal-cta" onClick={() => setTab('agreements')}>
            <span>You have {summary.pending_agreements} {summary.pending_agreements === 1 ? 'agreement' : 'agreements'} awaiting your signature.</span>
            <span className="portal-cta__go">Review →</span>
          </button>
        )}
        {summary?.pending_approvals > 0 && tab !== 'approvals' && (
          <button className="portal-banner portal-banner--warn portal-cta" onClick={() => setTab('approvals')}>
            <span>You have {summary.pending_approvals} purchase {summary.pending_approvals === 1 ? 'approval' : 'approvals'} awaiting your response.</span>
            <span className="portal-cta__go">Review →</span>
          </button>
        )}

        <nav className="pnav">
          {visibleTabs.map((t) => (
            <button key={t.key} className={'pnav__item' + (tab === t.key ? ' pnav__item--on' : '')} onClick={() => setTab(t.key)} aria-current={tab === t.key ? 'page' : undefined}>{t.label}</button>
          ))}
        </nav>

        <section className="panel">
          {tab === 'agreements' ? (
            loading ? <p className="hint">Loading…</p> : (
              agrOpen ? (
                <div className="pagr-view">
                  <button className="btn btn--sm" onClick={() => setAgrOpen(null)}>← Back</button>
                  <div className="pagr-doc">
                    {agrOpen.blocks.map((b, i) => (
                      b.h ? <h3 key={i} className="pagr-h">{b.h}</h3>
                        : b.li ? <ul key={i} className="pagr-li">{b.li.map((x, j) => <li key={j}>{x}</li>)}</ul>
                          : b.sign ? <div key={i} className="pagr-signline">Signature block follows on the PDF.</div>
                            : <p key={i} className="pagr-p">{b.p}</p>
                    ))}
                  </div>
                  {agrOpen.status === 'sent' ? (
                    <div className="pagr-sign">
                      <h3 className="form-section">Sign this agreement</h3>
                      <div className="field"><label>Full name</label>
                        <input value={agrName} onChange={(e) => setAgrName(e.target.value)} placeholder="Your name" /></div>
                      <label className="pagr-siglabel">Signature</label>
                      <div className="pagr-sigpad"><SignaturePad ref={agrSig} /></div>
                      <div className="pagr-sigbtns">
                        <button className="btn btn--sm" onClick={() => agrSig.current && agrSig.current.clear()}>Clear</button>
                        <button className="btn btn--primary" onClick={signAgreement} disabled={agrBusy}>{agrBusy ? 'Submitting…' : 'Agree & sign'}</button>
                      </div>
                      <p className="hint">By signing, you agree to the terms above. You'll receive a signed PDF copy.</p>
                      {agrErr && <div className="form-error">{agrErr}</div>}
                    </div>
                  ) : (
                    <div className="pagr-signed">
                      <span className="pappr__ok">Signed{agrOpen.client_signer_name ? ` by ${agrOpen.client_signer_name}` : ''}{agrOpen.signed_at ? ` · ${date(agrOpen.signed_at)}` : ''}</span>
                      <a className="btn btn--sm btn--primary" href={`/api/portal/agreements/${agrOpen.id}/pdf`}>Download signed PDF</a>
                    </div>
                  )}
                </div>
              ) : (
                agreements.length === 0 ? <p className="hint">No agreements.</p> : (
                  <div className="pagr-list">
                    {agreements.map((a) => (
                      <div className="pagr-row" key={a.id}>
                        <div className="pagr-row__main">
                          <span className="pagr-row__title">{a.title}</span>
                          <span className="pagr-row__meta">{a.status === 'signed' ? `Signed ${date(a.signed_at)}` : a.status === 'sent' ? 'Awaiting your signature' : a.status}</span>
                        </div>
                        <StatusBadge status={a.status} />
                        <button className="btn btn--sm btn--primary" onClick={() => openAgreement(a.id)}>{a.status === 'sent' ? 'Review & sign' : 'View'}</button>
                      </div>
                    ))}
                  </div>
                )
              )
            )
          ) : tab === 'approvals' ? (
            loading ? <p className="hint">Loading…</p> : (
              approvals.length === 0 ? <p className="hint">No purchase approvals.</p> : (
                <div className="papprovals">
                  {approvals.map((a) => {
                    const f = apprForm[a.id] || {};
                    return (
                      <div className={'pappr pappr--' + a.status} key={a.id}>
                        <div className="pappr__head">
                          <span className="pappr__title">{a.title}</span>
                          <StatusBadge status={a.status} />
                        </div>
                        {a.notes && <div className="pappr__notes">{a.notes}</div>}
                        <table className="pappr__items">
                          <tbody>
                            {a.items.map((it, i) => (
                              <tr key={i}>
                                <td>{it.description}{it.vendor ? <span className="pappr__vendor"> · {it.vendor}</span> : ''}</td>
                                <td className="num">{Number(it.quantity)}</td>
                                <td className="num">{money(it.unit_price)}</td>
                                <td className="num">{money(Number(it.quantity) * Number(it.unit_price))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="pappr__total"><span>Total</span><span className="num">{money(a.subtotal)}</span></div>

                        {a.status === 'pending' ? (
                          <div className="pappr__decide">
                            <div className="field"><label>Your name</label>
                              <input value={f.name ?? (portal?.customer_name || '')} onChange={(e) => setApprForm((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), name: e.target.value } }))} /></div>
                            <div className="field"><label>Comments (optional)</label>
                              <textarea rows={2} value={f.comments || ''} onChange={(e) => setApprForm((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), comments: e.target.value } }))} /></div>
                            <div className="pappr__btns">
                              <button className="btn btn--primary" disabled={apprBusy === a.id} onClick={() => decide(a.id, 'approve')}>{apprBusy === a.id ? 'Submitting…' : 'Approve'}</button>
                              <button className="btn btn--danger" disabled={apprBusy === a.id} onClick={() => decide(a.id, 'decline')}>Decline</button>
                            </div>
                            {f.err && <div className="form-error">{f.err}</div>}
                          </div>
                        ) : (
                          <div className="pappr__decided">
                            {a.status === 'approved' && <span className="pappr__ok">Approved{a.decided_by_name ? ` by ${a.decided_by_name}` : ''}{a.decided_at ? ` · ${date(a.decided_at)}` : ''}</span>}
                            {a.status === 'declined' && <span className="pappr__no">Declined{a.decided_by_name ? ` by ${a.decided_by_name}` : ''}{a.decided_at ? ` · ${date(a.decided_at)}` : ''}</span>}
                            {a.status === 'ordered' && <span className="pappr__ok">Approved &amp; ordered</span>}
                            {a.decision_comments && <span className="pappr__cmt">“{a.decision_comments}”</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )
          ) : tab === 'contracts' ? (
            loading ? <p className="hint">Loading…</p> : (
              contracts.length === 0 ? <p className="hint">No contracts on file.</p> : (
                <div className="pcontracts">
                  {contracts.map((c) => (
                    <div className="pcontract" key={c.id}>
                      <div className="pcontract__head">
                        <span className="pcontract__name">{c.name}</span>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="pcontract__meta">
                        {c.cadence === 'semi_monthly' ? 'Semi-monthly' : 'Monthly'}{c.start_date ? ` · since ${date(c.start_date)}` : ''}
                      </div>
                      {c.documents?.length > 0 ? (
                        <div className="pcontract__docs">
                          {c.documents.map((d) => (
                            <div className="pcontract__doc" key={d.id}>
                              <a href={`/api/portal/contracts/${d.id}/download?inline=1`} target="_blank" rel="noreferrer">{d.filename}</a>
                              <a className="btn btn--sm" href={`/api/portal/contracts/${d.id}/download`}>Download</a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="pcontract__none">A signed copy hasn’t been posted yet.</div>
                      )}
                    </div>
                  ))}
                </div>
              )
            )
          ) : tab === 'w9' ? (
            loading ? <p className="hint">Loading…</p> : (
              <div className="pw9">
                <p className="pw9__intro">Need our Form W-9 for your records or vendor setup? Request it here and we’ll review and send it.</p>
                {w9?.request?.status === 'pending' && (
                  <div className="pw9__status pw9__status--pending">Your W-9 request is pending review. We’ll email it once approved.</div>
                )}
                {w9?.request?.status === 'denied' && (
                  <div className="pw9__status pw9__status--denied">Your last request was declined{w9.request.decision_comments ? `: ${w9.request.decision_comments}` : '.'}</div>
                )}
                {w9?.request?.status === 'approved' && (
                  <div className="pw9__status pw9__status--approved">Your W-9 request was approved and emailed{w9.request.decision_comments ? ` — ${w9.request.decision_comments}` : '.'}</div>
                )}
                {w9?.document_available && (
                  <a className="btn btn--primary" href="/api/portal/w9-document" target="_blank" rel="noreferrer" style={{ marginBottom: 12, display: 'inline-block' }}>Download W-9 (PDF)</a>
                )}
                {w9?.request?.status !== 'pending' && (
                  <div className="pw9__form">
                    <label className="pw9__label">Add a note (optional)</label>
                    <textarea rows={2} value={w9Note} onChange={(e) => setW9Note(e.target.value)} placeholder="e.g. for our accounts-payable file" />
                    <button className="btn btn--primary" onClick={requestW9} disabled={w9Busy}>{w9Busy ? 'Submitting…' : (w9?.request ? 'Request again' : 'Request W-9')}</button>
                  </div>
                )}
                {w9Msg && <div className={w9Msg.ok ? 'pw9__status pw9__status--approved' : 'pw9__status pw9__status--denied'} style={{ marginTop: 10 }}>{w9Msg.text}</div>}
              </div>
            )
          ) : tab === 'site-visits' ? (
            loading ? <p className="hint">Loading…</p>
              : rows.length === 0 ? <p className="hint">No site visits yet.</p>
                : rows.map((j) => {
                  const done = (j.tasks || []).filter((t) => t.is_complete).length;
                  return (
                    <div className="pv-job" key={j.id}>
                      <div className="pv-job__head">
                        <span className="pv-job__title">{j.title || `Site visit ${j.job_number || ''}`}</span>
                        <span className="pv-job__meta">{done}/{(j.tasks || []).length} done{j.scheduled_date ? ` · ${date(j.scheduled_date)}` : ''}</span>
                      </div>
                      {j.location && <div className="pv-job__meta" style={{ marginBottom: 6 }}>{j.location}</div>}
                      {(j.tasks || []).length === 0 ? <p className="hint">No items listed yet.</p> : (j.tasks || []).map((t) => (
                        <div className={'pv-task' + (t.is_complete ? ' pv-task--done' : '')} key={t.id}>
                          <span className={'pv-task__mark' + (t.is_complete ? ' pv-task__mark--done' : '')}>{t.is_complete ? '✓' : ''}</span>
                          <div>
                            <div className="pv-task__title">{t.title}</div>
                            {t.notes && <div className="pv-task__notes">{t.notes}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
          ) : (
            <DataTable
              columns={columns} rows={rows} loading={loading}
              getRowKey={(r) => r.id || `${r.invoice_number}-${r.payment_date}`}
              onRowClick={tab === 'invoices' ? openInvoice : tab === 'quotes' ? openQuote : undefined}
              empty={emptyMsg}
            />
          )}
        </section>
      </main>

      <Drawer open={!!invoice} title={invoice && !invoice.loading ? invoice.invoice_number : 'Invoice'} width={620} onClose={() => setInvoice(null)}>
        {invLoading || (invoice && invoice.loading) ? <p className="hint">Loading…</p> : invoice && (
          <>
            <div className="inv-status-row">
              <StatusBadge status={invoice.status} />
              <span className="hint">Issued {date(invoice.issue_date)}{invoice.due_date ? ` · due ${date(invoice.due_date)}` : ''}</span>
            </div>

            <table className="mini-table">
              <thead><tr><th>Description</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {invoice.lines?.map((l, i) => (
                  <tr key={i}><td>{l.description}</td><td className="num">{l.quantity}</td><td className="num">{money(l.unit_price)}</td><td className="num">{money(l.amount)}</td></tr>
                ))}
              </tbody>
            </table>

            <div className="totals">
              <div className="totals__row"><span>Subtotal</span><span className="num">{money(invoice.subtotal)}</span></div>
              <div className="totals__row"><span>Tax</span><span className="num">{money(invoice.tax_total)}</span></div>
              <div className="totals__row totals__row--grand"><span>Total</span><span className="num">{money(invoice.total)}</span></div>
              <div className="totals__row"><span>Paid</span><span className="num">{money(invoice.amount_paid)}</span></div>
              <div className="totals__row totals__row--grand"><span>Balance due</span><span className="num">{money(invoice.balance)}</span></div>
            </div>

            {quote?.payable && (
              <div className="pay-box">
                <h3 className="form-section">Pay this invoice</h3>
                <p className="hint">A processing fee is added to cover payment costs. Bank transfer (ACH) is the lowest-cost option.</p>
                <button className="btn btn--primary pay-opt" disabled={!!paying} onClick={() => startPay('ach')}>
                  <span>{paying === 'ach' ? 'Starting…' : 'Pay by bank (ACH)'}</span>
                  <span className="num">{money(quote.ach.total)}</span>
                </button>
                <div className="pay-opt__note">{money(invoice.balance)} balance + {money(quote.ach.fee)} fee</div>
                <button className="btn pay-opt" disabled={!!paying} onClick={() => startPay('card')}>
                  <span>{paying === 'card' ? 'Starting…' : 'Pay by card'}</span>
                  <span className="num">{money(quote.card.total)}</span>
                </button>
                <div className="pay-opt__note">{money(invoice.balance)} balance + {money(quote.card.fee)} fee</div>
                <p className="hint">You'll be taken to our secure payment page to finish.</p>
              </div>
            )}

            {invoice.payments?.length > 0 && (
              <>
                <h3 className="form-section">Payments applied</h3>
                {invoice.payments.map((p, i) => (
                  <div className="runs__row" key={i}>
                    <span>{date(p.payment_date)}</span><span>{(p.method || '').toUpperCase()}</span>
                    <span className="num">{money(p.amount)}</span>
                  </div>
                ))}
              </>
            )}

            {invoice.expenses?.length > 0 && (
              <>
                <h3 className="form-section">Billed expenses</h3>
                {invoice.expenses.map((e, i) => (
                  <div className="runs__row" key={i}>
                    <span>{date(e.expense_date)}</span><span>{e.vendor || '—'}</span>
                    <span className="num">{money(e.total)}</span>
                  </div>
                ))}
              </>
            )}

            {invoice.terms && <><h3 className="form-section">Terms</h3><p className="hint">{invoice.terms}</p></>}
          </>
        )}
      </Drawer>

      <Drawer open={!!quoteDoc} title={quoteDoc && !quoteDoc.loading ? quoteDoc.quote_number : 'Quote'} width={620} onClose={() => setQuoteDoc(null)}>
        {quoteLoading || (quoteDoc && quoteDoc.loading) ? <p className="hint">Loading…</p> : quoteDoc && (
          <>
            <div className="inv-status-row">
              <StatusBadge status={quoteDoc.status} />
              <span className="hint">Issued {date(quoteDoc.issue_date)}{quoteDoc.expiry_date ? ` · valid until ${date(quoteDoc.expiry_date)}` : ''}</span>
            </div>

            <table className="mini-table">
              <thead><tr><th>Description</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {quoteDoc.lines?.map((l, i) => (
                  <tr key={i}><td>{l.description}</td><td className="num">{l.quantity}</td><td className="num">{money(l.unit_price)}</td><td className="num">{money(l.amount)}</td></tr>
                ))}
              </tbody>
            </table>

            <div className="totals">
              <div className="totals__row"><span>Subtotal</span><span className="num">{money(quoteDoc.subtotal)}</span></div>
              <div className="totals__row"><span>Tax</span><span className="num">{money(quoteDoc.tax_total)}</span></div>
              <div className="totals__row totals__row--grand"><span>Total</span><span className="num">{money(quoteDoc.total)}</span></div>
            </div>

            {quoteDoc.terms && <><h3 className="form-section">Terms</h3><p className="hint" style={{ whiteSpace: 'pre-wrap' }}>{quoteDoc.terms}</p></>}

            <div style={{ marginTop: 16 }}>
              <a className="btn btn--primary" href={`/api/portal/quotes/${quoteDoc.id}/pdf`}>Download PDF</a>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
